import { useState, useEffect } from 'react';
import { useOnboarding } from '../OnboardingContext';
import { User, FileCheck, AlertCircle } from 'lucide-react';
import { useScrollToActivate } from '@/hooks/useScrollToActivate';

export function PersonalInfoStep() {
  const { data, updateData, goToNextStep } = useOnboarding();
  const { canActivate } = useScrollToActivate();
  const [firstName, setFirstName] = useState(data.firstName);
  const [lastName, setLastName] = useState(data.lastName);

  // Pre-fill with extracted data from document verification
  useEffect(() => {
    if (data.extractedFirstName && !data.firstName) {
      setFirstName(data.extractedFirstName);
    }
    if (data.extractedLastName && !data.lastName) {
      setLastName(data.extractedLastName);
    }
  }, [data.extractedFirstName, data.extractedLastName, data.firstName, data.lastName]);

  const hasExtractedData = data.extractedFirstName || data.extractedLastName;
  const isModified = (data.extractedFirstName && firstName !== data.extractedFirstName) || 
                     (data.extractedLastName && lastName !== data.extractedLastName);

  const handleContinue = () => {
    if (firstName.trim() && lastName.trim()) {
      updateData({ firstName: firstName.trim(), lastName: lastName.trim() });
      goToNextStep();
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col pt-24 pb-28 px-4 sm:px-6">
      <div className="flex-1 flex flex-col max-w-md mx-auto w-full overflow-y-auto">
        <div className="mb-6">
          <div className="w-16 h-16 rounded-2xl bg-accent/10 flex items-center justify-center mb-6">
            <User className="w-8 h-8 text-accent" />
          </div>
          <h1 className="text-2xl font-bold text-foreground mb-2">
            Confirm your name
          </h1>
          <p className="text-muted-foreground">
            {hasExtractedData 
              ? "We've extracted your name from your ID. Please confirm or correct if needed."
              : "Please enter your name as it appears on your official documents."
            }
          </p>
        </div>

        {hasExtractedData && (
          <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-accent/10 border border-accent/20 mb-6">
            <FileCheck className="w-5 h-5 text-accent shrink-0" />
            <p className="text-sm text-foreground">
              Pre-filled from your ID document
            </p>
          </div>
        )}

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              First name
            </label>
            <input
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="e.g. John"
              className="input-field"
              autoFocus
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Last name
            </label>
            <input
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder="e.g. Smith"
              className="input-field"
            />
          </div>
        </div>

        {isModified && (
          <div className="flex items-start gap-2 px-4 py-3 rounded-xl bg-amber-500/10 border border-amber-500/20 mt-4">
            <AlertCircle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
            <p className="text-sm text-foreground">
              You've modified the extracted data. Please ensure this matches your official ID.
            </p>
          </div>
        )}

      </div>
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-background border-t border-border">
        <div className="max-w-md mx-auto">
          <button
            onClick={handleContinue}
            disabled={!firstName.trim() || !lastName.trim() || !canActivate}
            className="w-full h-14 text-lg font-semibold rounded-xl bg-accent hover:bg-accent/90 text-accent-foreground disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            {hasExtractedData ? 'Confirm & Continue' : 'Continue'}
          </button>
        </div>
      </div>
    </div>
  );
}
